import { Alert, Button, Dialog, Link, Tag } from '@1k5-tech-es/grado-react';
import { Mail } from 'lucide-react';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';

import { estimateDuration, plural, type SendPlan } from './emails';

const VISIBLE_NAMES = 8;

/** "Are you sure?" before any email leaves: who receives it, who does not, and how it goes out. */
export function SendConfirmDialog({
  open,
  plan,
  quarter,
  intervalSeconds,
  from,
  redirectTo,
  sending,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean;
  plan: SendPlan | null;
  quarter: string;
  intervalSeconds: number;
  from: string;
  redirectTo: string | null;
  sending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [showAll, setShowAll] = useState(false);
  if (!plan) return null;
  const count = plan.recipients.length;
  const single = count === 1 ? plan.recipients[0]! : null;
  const names = showAll ? plan.recipients : plan.recipients.slice(0, VISIBLE_NAMES);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && !sending && onClose()}
      title={single ? 'Enviar el informe por correo' : 'Enviar los informes por correo'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={sending}>
            Cancelar
          </Button>
          <Button icon={Mail} onClick={onConfirm} loading={sending} disabled={count === 0}>
            {sending ? 'Enviando…' : single ? `Sí, enviar a ${single.fullName}` : `Sí, enviar a ${count} asesores`}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm text-muted-foreground">
        {redirectTo && <TestModeBanner redirectTo={redirectTo} />}
        <div className="flex gap-4 rounded-lg bg-primary-subtle p-4">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground" aria-hidden>
            <Mail className="size-5" />
          </span>
          <div className="min-w-0">
            {single ? (
              <p className="text-foreground">
                Se va a enviar a <strong>{single.fullName}</strong> su informe de <strong>{quarter}</strong>
                {' '}a <span className="font-medium break-all">{single.email}</span>.
              </p>
            ) : (
              <p className="text-foreground">
                Se va a enviar el informe de <strong>{quarter}</strong> a{' '}
                <strong className="text-primary-text">{count} asesores</strong>. Cada uno recibe solo el suyo.
              </p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">Desde {from}</p>
          </div>
        </div>

        {!single && count > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            {names.map((a) => (
              <Tag key={a.id} size="sm">
                {a.fullName}
              </Tag>
            ))}
            {plan.recipients.length > names.length && (
              <Button variant="link" size="sm" onClick={() => setShowAll(true)}>
                +{plan.recipients.length - names.length} más
              </Button>
            )}
          </div>
        )}

        {plan.resend.length > 0 && (
          <Warning>
            {plan.resend.length === 1 && single
              ? 'Ya se le envió este informe antes: lo recibirá otra vez.'
              : `${plan.resend.length} ya lo recibieron antes y lo recibirán otra vez: ${plan.resend.map((a) => a.fullName).join(', ')}.`}
          </Warning>
        )}
        {plan.withoutEmail.length > 0 && (
          <Warning>
            {plan.withoutEmail.length === 1 ? 'No tiene email y no lo recibirá: ' : `${plan.withoutEmail.length} no tienen email y no lo recibirán: `}
            {plan.withoutEmail.map((a) => a.fullName).join(', ')}.{' '}
            <Link as={RouterLink} to="/asesores" underline="always" className="font-semibold">
              Añadir en Asesores
            </Link>
          </Warning>
        )}
        {plan.alreadyInQueue.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {plan.alreadyInQueue.length} ya están en la cola de envío y no se duplican.
          </p>
        )}

        {count > 1 && (
          <p className="text-xs text-muted-foreground">
            Se envían poco a poco, uno cada {plural(intervalSeconds, 'segundo', 'segundos')}, para no saturar el servidor de correo: tardará{' '}
            {estimateDuration(count, intervalSeconds)}. Puedes cerrar esta página; el envío continúa.
          </p>
        )}
        {error && (
          <Alert tone="danger" title="No se ha enviado">
            {error}
          </Alert>
        )}
      </div>
    </Dialog>
  );
}

export function TestModeBanner({ redirectTo }: { redirectTo: string }) {
  return (
    <Alert tone="info" title="Modo prueba">
      Todos los correos irán a <span className="break-all">{redirectTo}</span>, no a los asesores.
    </Alert>
  );
}

function Warning({ children }: { children: React.ReactNode }) {
  return (
    <Alert tone="warning" title="Antes de enviar" role="none">
      {children}
    </Alert>
  );
}
